import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, ConfirmDeleteModal, EmptyState, Skeleton } from '../components/ui'
import { useCredential, useEvents, useLocationTree, usePersons } from '../api/hooks'
import { api } from '../api/client'
import type { CommandLog } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { initials, relativeTime } from '../lib/format'
import { locationNames } from '../lib/locations'
import { readOnlyReason } from '../lib/dryrun'
import { fmtDate } from './Directory'
import { CommandResultView, credentialStatusTone, kindLabel, lastUsedSummary, lastUsedTitle } from './Credentials'
import { LocationLink } from '../components/LocationLink'

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-ostrel-line py-2 last:border-0">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {label}
      </span>
      <span className="tnum text-right text-sm text-ostrel-ink">{children}</span>
    </div>
  )
}

export default function CredentialDetailPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const credential = useCredential(uid)
  const persons = usePersons()
  const tree = useLocationTree()
  const [since7d] = useState(() => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString())
  const events = useEvents({ credential: uid, since: since7d })

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [lastCommand, setLastCommand] = useState<CommandLog | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  const block = useMutation({
    mutationFn: () => api.blockCredential(uid),
    onSuccess: (cmd) => {
      setLastCommand(cmd)
      invalidate()
    },
  })
  const unblock = useMutation({
    mutationFn: () => api.unblockCredential(uid),
    onSuccess: (cmd) => {
      setLastCommand(cmd)
      invalidate()
    },
  })
  const reportLost = useMutation({
    // PATCH, not PUT: PUT is full-replace (kind + code required), so a
    // status-only PUT 422'd — and the code is masked for operators anyway.
    mutationFn: () => api.patchCredential(uid, { status: 'lost' }),
    onSuccess: (cred) => {
      setLastCommand(cred.command)
      invalidate()
    },
  })
  const remove = useMutation({
    mutationFn: () => api.deleteCredential(uid),
    onSuccess: () => {
      invalidate()
      navigate('/credentials')
    },
  })
  const mutating = block.isPending || unblock.isPending || reportLost.isPending

  const locNames = tree.data ? locationNames(tree.data) : new Map<string, string>()
  const personNames = new Map((persons.data ?? []).map((p) => [p.unique_id, p]))
  const usage = (events.data?.items ?? []).filter((e) => e.credential === uid).slice(0, 8)

  if (credential.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-32" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
        </div>
      </div>
    )
  }

  if (credential.isError) {
    return (
      <div className="flex flex-col gap-4">
        <p className="tnum flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
          <Link to="/credentials" className="hover:text-ostrel-ink">
            ← Credentials
          </Link>
          <span>/</span>
          <span className="font-mono">{uid}</span>
        </p>
        <EmptyState
          title="Could not load credential"
          hint={credential.error.message}
          action={
            <Link
              to="/credentials"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              ← Back to credentials
            </Link>
          }
        />
      </div>
    )
  }

  const c = credential.data
  // A credential bound to a dry-run system is read-only.
  const credReadOnly = readOnlyReason(c)
  const holder = c.person ? personNames.get(c.person) : undefined

  return (
    <div className="flex flex-col gap-4">
      <p className="tnum flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
        <Link to="/credentials" className="hover:text-ostrel-ink">
          ← Credentials
        </Link>
        <span>/</span>
        <span className="uppercase">{kindLabel(c.kind)}</span>
      </p>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
              {kindLabel(c.kind)} · <span className="tnum font-mono normal-case">{c.unique_id}</span>
            </p>
            <h1 className="tnum mt-1 font-display text-3xl text-ostrel-ink">{c.code}</h1>
            <p className="mt-1 text-[11px] text-ostrel-ink-muted">
              {holder ? (holder.display_name || c.person) : c.person ? c.person : 'Unassigned'}
              {holder?.valid_to ? ` · holder valid until ${fmtDate(holder.valid_to)}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={credentialStatusTone(c.status)} label={c.status} />
            {c.status === 'blocked' ? (
              <Button
                variant="navy"
                size="sm"
                disabled={mutating || credReadOnly !== undefined}
                title={credReadOnly}
                onClick={() => unblock.mutate()}
              >
                Unblock
              </Button>
            ) : (
              <Button
                variant="navy"
                size="sm"
                disabled={mutating || credReadOnly !== undefined}
                title={credReadOnly}
                onClick={() => block.mutate()}
              >
                Block credential
              </Button>
            )}
            {c.status === 'active' && (
              <Button
                variant="danger"
                size="sm"
                disabled={mutating || credReadOnly !== undefined}
                title={credReadOnly}
                onClick={() => reportLost.mutate()}
              >
                Report lost
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={credReadOnly !== undefined}
              title={credReadOnly}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </div>
        </div>
        {(c.unenforced_systems ?? []).length > 0 && (
          <p
            role="alert"
            className="mt-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger"
          >
            This credential must be disabled, but its card is adopted as external at{' '}
            {(c.unenforced_systems ?? []).map((s) => s.name).join(', ')}: Ostrel can&apos;t switch it off there. Adopt
            the card as managed, or switch it off in that system.
          </p>
        )}
        {(block.isError || unblock.isError || reportLost.isError) && (
          <p className="mt-3 text-xs text-ostrel-danger">
            {(block.error ?? unblock.error ?? reportLost.error)?.message}
          </p>
        )}
      </Card>

      {lastCommand && <CommandResultView command={lastCommand} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Holder" className="self-start">
          {persons.isPending ? (
            <Skeleton className="h-12" />
          ) : !c.person ? (
            <EmptyState title="Unassigned" hint="This credential is not linked to a person." />
          ) : (
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-ostrel-navy text-[11px] font-semibold text-ostrel-gold">
                {initials(holder?.display_name || '?')}
              </span>
              <span className="min-w-0 flex-1">
                <Link
                  to={`/directory/${c.person}`}
                  className="block truncate text-sm font-medium text-ostrel-ink hover:text-ostrel-accent"
                >
                  {holder?.display_name || c.person}
                </Link>
                <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">{c.person}</span>
              </span>
            </div>
          )}
        </Card>

        <Card title="Details" className="self-start">
          <DetailRow label="Kind">{kindLabel(c.kind)}</DetailRow>
          <DetailRow label="Type">{c.subtype || '—'}</DetailRow>
          {/* The credential's global last-used moment (issue #51) — not
              scoped to the 7-day "Usage history" window below, so a tag
              quiet for 10+ days still shows where/when it was last seen
              instead of a misleading "no usage". */}
          <DetailRow label="Last used">
            <span title={lastUsedTitle(c)}>{lastUsedSummary(c)}</span>
          </DetailRow>
          <DetailRow label="Status">{c.status}</DetailRow>
        </Card>

        <Card title="Usage history · 7 days" className="self-start">
          {events.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : events.isError ? (
            <EmptyState title="Could not load usage" hint={events.error.message} />
          ) : usage.length === 0 ? (
            <EmptyState title="No recent usage" hint="Events from the last 7 days involving this credential appear here." />
          ) : (
            <ul className="flex flex-col divide-y divide-ostrel-line">
              {usage.map((e) => (
                <li key={e.unique_id} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
                  <span className="tnum w-16 shrink-0 pt-0.5 text-[10px] text-ostrel-ink-muted">
                    {relativeTime(e.occurred_at)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-ostrel-ink">
                      {e.type.replace(/_/g, ' ')}
                    </span>
                    <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                      {e.location ? (
                        <LocationLink uid={e.location}>{locNames.get(e.location) ?? e.location}</LocationLink>
                      ) : (
                        (e.device ?? e.unique_id)
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete credential"
        objectName={c.code}
        expected={c.unique_id}
        pending={remove.isPending}
        error={remove.isError ? remove.error.message : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete credential'}
        onConfirm={() => remove.mutate()}
        onClose={() => setDeleteOpen(false)}
      >
        <p>
          Delete credential <span className="tnum font-mono font-medium">{c.code}</span> ({c.unique_id})?
          This cannot be undone.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
